// CameraOcclusion — professional third-person camera visibility guard.
//
// Every frame we cast TWO short rays: camera -> challenger world-pos and
// camera -> opponent world-pos. Any mesh flagged `userData.occludable=true`
// that lies BETWEEN the camera and either creature is smoothly faded down
// so the player never loses sight of the action.
//
// Meshes tagged with `userData.small=true` (foliage, grass) fade to 0 so
// they visually disappear. Larger obstacles (trees, boulders) fade to 0.28
// so the player still perceives depth cues but sees through them.
//
// This runs alongside CinematicCamera in the R3F frame loop but never
// touches the camera itself — CinematicCamera continues to auto-fit and
// pick angles, while THIS component solves the "tree in front" problem.
import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

const FADED_LARGE = 0.28;   // trees, boulders, cacti — see-through
const FADED_SMALL = 0.0;    // grass tufts, tiny foliage — hide entirely
const FADE_LERP = 8.5;       // opacity ease rate (higher = snappier)
const CREATURE_RADIUS = 0.9; // ignore hits within this radius of the creature (the creature itself sits at target)
const MAX_HITS = 24;

export default function CameraOcclusion({ chPosition, opPosition, targetY = 0.9, enabled = true }) {
  const { scene, camera } = useThree();

  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const dir = useMemo(() => new THREE.Vector3(), []);
  const origin = useMemo(() => new THREE.Vector3(), []);
  const target = useMemo(() => new THREE.Vector3(), []);

  const occlusionSet = useRef(new Set());

  useFrame((_, dt) => {
    if (!enabled) return;

    // -------- Rebuild occlusion set for this frame --------
    const hits = occlusionSet.current;
    hits.clear();

    origin.copy(camera.position);

    // Ray helper — casts from camera to (x, targetY, z) and records
    // occludable meshes lying strictly between camera and creature.
    const castTo = (x, y, z) => {
      target.set(x, y, z);
      dir.copy(target).sub(origin);
      const dist = dir.length();
      if (dist < 0.001) return;
      dir.divideScalar(dist);
      raycaster.set(origin, dir);
      raycaster.near = 0;
      raycaster.far = Math.max(0, dist - CREATURE_RADIUS);
      const results = raycaster.intersectObject(scene, true);
      let count = 0;
      for (const hit of results) {
        if (count++ >= MAX_HITS) break;
        const ud = hit.object?.userData;
        if (ud && ud.occludable) hits.add(hit.object);
      }
    };

    castTo(chPosition[0], chPosition[1] + targetY, chPosition[2]);
    castTo(opPosition[0], opPosition[1] + targetY, opPosition[2]);

    // -------- Smoothly ease every occludable mesh toward its target --------
    // We traverse the scene once, ONLY visiting meshes flagged occludable.
    // (This is bounded by the arena's dressing size — ~50–80 meshes.)
    const lerpAmt = Math.min(1, FADE_LERP * dt);
    scene.traverse((obj) => {
      const ud = obj.userData;
      if (!ud || !ud.occludable || !obj.material) return;
      const desired = hits.has(obj)
        ? (ud.small ? FADED_SMALL : FADED_LARGE)
        : 1;
      // material can be an array (multi-material meshes); handle both.
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) {
        if (m.opacity === undefined) continue;
        m.transparent = true;
        m.opacity = THREE.MathUtils.lerp(m.opacity, desired, lerpAmt);
        // depthWrite off when we've faded significantly so we don't
        // punch a hole through creatures rendered behind us.
        m.depthWrite = m.opacity > 0.85;
        // Fully-faded small foliage becomes non-interactive.
        m.visible = m.opacity > 0.02;
      }
    });
  });

  return null;
}
